/**
 * افزودنِ یک کاندیدای دستی.
 *
 * ⚠️ این تنها نوشتنِ **فوریِ** این سربرگ است و بقیه در صف می‌نشینند. دلیلش
 * ساختاری است نه سلیقه‌ای: `crawler.source_drafts` هیچ نامتغیرِ محاسباتی
 * ندارد — نه هشِ رفتاری، نه ادعای دامنه، نه نسخه — پس یک ردیفِ دستی از نظرِ
 * `crawl promote` دقیقاً همان‌قدر معتبر است که ردیفِ مدل. همان چیزی که
 * `discovery/CLAUDE.md` می‌گوید: «اینکه از API آمده یا از یک نشستِ دستی،
 * هیچ چیزِ دیگری را عوض نمی‌کند».
 *
 * ردیفِ تازه `enabled: false` است (قانون D1 — هیچ‌چیز خودبه‌خود روشن
 * نمی‌شود) و تا وقتی `inspect` نشود حکمی نمی‌گیرد. پس قدمِ بعدی همیشه
 * «بررسی» است، و دکمه‌اش همان‌جا کنارِ ردیف است.
 */
import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { sourceRepo, type PipelineCity } from '@/core/data/sourceRepo';
import { hostOf } from '@/core/lib/sourcePipeline';
import { reportError } from '@/core/lib/report';

export function AddDraftForm({ cities, defaultCity, onDone }: {
  cities: PipelineCity[];
  defaultCity?: string;
  onDone: (message: string) => void;
}) {
  const [city, setCity] = useState(defaultCity || cities[0]?.city || '');
  const [name, setName] = useState('');
  const [label, setLabel] = useState('');
  const [urls, setUrls] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const lines = urls.split('\n').map((u) => u.trim()).filter(Boolean);
  // همان قاعده‌ی دیتابیس، ولی قبل از فرستادن — یک شناسه‌ی غلط نباید با
  // خطای پستگرس جواب بگیرد.
  const nameOk = /^[a-z0-9_]+$/.test(name);
  const ready = city && nameOk && label.trim() && lines.length > 0;

  const add = useMutation({
    mutationFn: () => sourceRepo.addDraft({
      city, name, label: label.trim(), urls: lines, note: note.trim() || null,
    }),
    onSuccess: (r) => onDone(`پیش‌نویسِ ${r.bare} ثبت شد — حالا «بررسی» بزن.`),
    onError: (err) => {
      reportError('admin.sources.addDraft', err);
      setError(err instanceof Error ? err.message : String(err));
    },
  });

  return (
    <>
      <div className="filters" style={{ marginBottom: 8 }}>
        <label className="dim" htmlFor="add-city">شهر</label>
        <select id="add-city" value={city} onChange={(e) => setCity(e.target.value)}>
          {cities.map((c) => <option key={c.city} value={c.city}>{c.label}</option>)}
        </select>
      </div>

      <input className="input" value={name} dir="ltr"
        placeholder="شناسه — حروفِ کوچک، رقم، زیرخط (مثل ottawa_jazzfest)"
        onChange={(e) => setName(e.target.value)} />
      {name && !nameOk ? (
        <p className="err">شناسه فقط حروفِ کوچکِ لاتین، رقم و زیرخط.</p>
      ) : null}

      <input className="input" value={label} placeholder="نامِ نمایشی"
        onChange={(e) => setLabel(e.target.value)} />

      <textarea className="input textarea" value={urls} dir="ltr"
        placeholder={'یک آدرس در هر خط\nhttps://example.org/events'}
        onChange={(e) => setUrls(e.target.value)} />
      {lines.length > 0 ? (
        <p className="dim">دامنه: <span className="mono">{hostOf(lines[0])}</span></p>
      ) : null}

      <textarea className="input textarea" value={note}
        placeholder="چرا این سایت؟ (همان چیزی که در یادداشتِ منبع می‌نشیند)"
        onChange={(e) => setNote(e.target.value)} />

      {error ? <p className="err">{error}</p> : null}

      <button className="btn block" type="button" disabled={!ready || add.isPending}
        onClick={() => { setError(null); add.mutate(); }}>
        {add.isPending ? '…' : 'ثبتِ پیش‌نویس'}
      </button>
    </>
  );
}
